import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Target, TrendingUp, Users, Sparkles, ChevronRight } from "lucide-react";
import { IMAGES } from "@/data/site";
import { api } from "@/lib/api";

const SERVICES = [
  { icon: Target, title: "Scouting", text: "Identificação, observação e avaliação de jogadores através de análise presencial, vídeo e desempenho competitivo." },
  { icon: TrendingUp, title: "Desenvolvimento do Jogador", text: "Definição de objetivos, acompanhamento individual e apoio contínuo na evolução técnica, competitiva e pessoal." },
  { icon: Users, title: "Gestão de Carreira", text: "Planeamento do percurso profissional, ligação com clubes e acompanhamento das principais decisões da carreira." },
  { icon: Sparkles, title: "Branding Pessoal", text: "Construção da identidade profissional do atleta, portefólios, vídeos, imagem digital e comunicação." },
];

const METHOD = [
  { n: "01", title: "Identificação", text: "Observação contínua de jogadores através de scouting presencial e análise de desempenho." },
  { n: "02", title: "Avaliação", text: "Elaboração de relatórios técnicos e identificação do potencial de desenvolvimento." },
  { n: "03", title: "Desenvolvimento", text: "Acompanhamento individual do atleta e definição de objetivos desportivos." },
  { n: "04", title: "Promoção", text: "Apresentação dos jogadores a clubes, academias e parceiros estratégicos." },
  { n: "05", title: "Acompanhamento", text: "Monitorização contínua da evolução desportiva e apoio ao longo da carreira." },
];

const WHY = [
  "Acompanhamento personalizado",
  "Comunicação transparente",
  "Relatórios profissionais de scouting",
  "Avaliação objetiva dos jogadores",
  "Desenvolvimento contínuo",
  "Rede de contactos no futebol",
  "Compromisso com relações de longo prazo",
  "Foco na evolução do atleta",
  "Criação de valor para os clubes",
];

const PARTNERS = [
  "Clubes profissionais", "Clubes de formação", "Academias de futebol", "Treinadores",
  "Departamentos de scouting", "Agentes FIFA", "Organizações desportivas", "Parceiros especializados",
];

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [news, setNews] = useState([]);
  const [breaking, setBreaking] = useState(null);

  useEffect(() => {
    api.get("/players/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/news", { params: { limit: 6 } }).then((r) => {
      setNews(r.data.slice(0, 3));
      const b = r.data.find((n) => n.slug === "bruno-oficializado-estrela-amadora-b") || r.data[0];
      if (b) setBreaking(b);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* HERO */}
      <section data-testid="home-hero" className="relative h-[100vh] min-h-[700px] w-full overflow-hidden">
        <img src={IMAGES.heroTunnel} alt="Jogador em túnel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="grain-overlay" />

        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-32">
          <div className="max-w-3xl overflow-hidden">
            <div className="section-eyebrow mb-6 reveal in">AF Legacy Sports · Agência de Futebol</div>
            <h1 className="font-cinzel uppercase text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight break-words">
              We don't manage <br className="hidden md:block" />
              players<br />
              <span className="text-[#A67C00]">We build legacy</span>
            </h1>
            <p className="text-neutral-300 text-base md:text-lg mt-8 max-w-xl leading-relaxed">
              Scouting, desenvolvimento de jogadores e gestão de carreira no futebol.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link to="/submeter-perfil" data-testid="hero-submit-btn" className="btn-gold">
                Submeter perfil <ArrowRight size={16} />
              </Link>
              <Link to="/clubes-parceiros" data-testid="hero-club-btn" className="btn-outline">
                Represento um clube
              </Link>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3 text-neutral-400 text-[0.65rem] tracking-[0.4em] uppercase absolute bottom-10 right-10">
            <span>Scroll</span>
            <ArrowDown size={14} className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* BREAKING ANNOUNCEMENT */}
      {breaking && (
        <section data-testid="breaking-announcement" className="relative border-y border-[#A67C00]/40 bg-[#0F0F0F] overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #A67C00 0%, transparent 45%)" }} />
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16 grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2 relative aspect-[3/4] max-h-[560px] overflow-hidden bg-black">
              <img
                src={breaking.image}
                alt={breaking.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-[#A67C00] text-white text-[0.6rem] tracking-[0.3em] uppercase px-3 py-1.5 font-medium">
                Última hora
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="section-eyebrow mb-5 flex items-center gap-3">
                <span className="w-2 h-2 bg-[#A67C00] rounded-full animate-pulse" />
                <span>Destaque · {new Date(breaking.date).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}</span>
              </div>
              <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.1] tracking-tight mb-6">
                {breaking.title}
              </h2>
              <p className="text-neutral-300 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
                {breaking.summary}
              </p>
              <Link
                to={`/legacy-news/${breaking.slug}`}
                data-testid="breaking-cta"
                className="btn-gold"
              >
                Ler anúncio completo <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* APRESENTAÇÃO */}
      <section className="relative py-28 md:py-40 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="section-eyebrow mb-6">01 — Apresentação</div>
            <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-10">
              Talento precisa <br /><span className="text-[#A67C00]">de direção</span>
            </h2>
            <p className="text-neutral-300 text-base leading-relaxed mb-6">
              A AF Legacy Sports é uma agência especializada em scouting, desenvolvimento
              de jogadores e gestão de carreira, dedicada a identificar talento, potenciar
              o crescimento dos atletas e criar oportunidades junto de clubes e parceiros
              nacionais e internacionais.
            </p>
            <p className="text-neutral-400 text-base leading-relaxed mb-10">
              Trabalhamos de forma personalizada, acompanhando cada jogador ao longo do
              seu percurso desportivo, com profissionalismo, transparência e compromisso.
            </p>
            <Link to="/sobre-nos" data-testid="home-about-btn" className="btn-outline">
              Conhecer a agência <ArrowRight size={14} />
            </Link>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden zoom-parent reveal">
            <img src={IMAGES.scoutingWork} alt="Scouting" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 border border-[#A67C00]/20" />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
              <div className="section-eyebrow">Scouting & Análise</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section className="relative py-28 md:py-40 bg-[#0F0F0F] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 reveal">
            <div>
              <div className="section-eyebrow mb-6">02 — Serviços</div>
              <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight">
                O que fazemos
              </h2>
            </div>
            <Link to="/servicos" className="btn-outline self-start" data-testid="home-services-btn">
              Todos os serviços <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {SERVICES.map((s, i) => (
              <div key={s.title} data-testid={`service-card-${i}`} className="bg-[#0B0B0B] p-10 hover:bg-[#141414] transition-colors group reveal">
                <s.icon size={28} className="text-[#A67C00] mb-8" strokeWidth={1.2} />
                <div className="text-[#A67C00] text-xs tracking-[0.3em] uppercase mb-3">0{i + 1}</div>
                <h3 className="font-cinzel uppercase text-white text-xl mb-4 tracking-wide">{s.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MÉTODO */}
      <section className="relative py-28 md:py-40 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="reveal max-w-3xl mb-16">
            <div className="section-eyebrow mb-6">03 — Método</div>
            <h2 className="font-cinzel uppercase text-white text-3xl md:text-5xl font-semibold leading-tight tracking-tight">
              Um método construído para <span className="text-[#A67C00]">desenvolver talento</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {METHOD.map((m, i) => (
              <div key={m.n} data-testid={`method-step-${i}`} className="bg-[#0B0B0B] p-8 hover:bg-[#141414] transition-colors reveal">
                <div className="font-cinzel text-[#A67C00] text-4xl mb-8">{m.n}</div>
                <h3 className="font-cinzel uppercase text-white text-lg mb-3 tracking-wide">{m.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PLAYER */}
      <section className="relative py-28 md:py-40 bg-black border-b border-white/5 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2 relative aspect-[3/4] overflow-hidden zoom-parent reveal">
            <img src={featured?.photo || IMAGES.athletePortrait} alt={featured?.name || "Jogador em destaque"} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute top-4 left-4 bg-[#A67C00] px-3 py-1.5 text-white text-[0.65rem] tracking-[0.3em] uppercase">
              Destaque
            </div>
          </div>
          <div className="lg:col-span-3 reveal">
            <div className="section-eyebrow mb-6">04 — Talento em destaque</div>
            {featured ? (
              <>
                <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-2">
                  {featured.name}
                </h2>
                <div className="text-[#A67C00] text-sm tracking-[0.25em] uppercase mb-8">
                  {featured.position} · {featured.age_group} · {featured.nationality}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 mb-8 border border-white/5">
                  {[
                    ["Clube", featured.current_club],
                    ["Altura", featured.height_cm ? `${featured.height_cm} cm` : "-"],
                    ["Peso", featured.weight_kg ? `${featured.weight_kg} kg` : "-"],
                    ["Pé", featured.dominant_foot],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-[#0B0B0B] p-5">
                      <div className="text-neutral-500 text-[0.65rem] tracking-[0.3em] uppercase mb-2">{k}</div>
                      <div className="text-white text-sm">{v || "-"}</div>
                    </div>
                  ))}
                </div>

                <p className="text-neutral-300 text-base leading-relaxed mb-8">{featured.description}</p>

                {featured.achievements?.length > 0 && (
                  <ul className="space-y-2 mb-10">
                    {featured.achievements.slice(0, 4).map((a) => (
                      <li key={a} className="flex items-start gap-3 text-neutral-300 text-sm">
                        <ChevronRight size={14} className="text-[#A67C00] mt-1 flex-shrink-0" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <Link to={`/jogadores/${featured.slug}`} data-testid="featured-player-btn" className="btn-gold">
                  Ver perfil completo <ArrowRight size={14} />
                </Link>
              </>
            ) : (
              <>
                <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
                  Novo talento em breve
                </h2>
                <p className="text-neutral-300 text-base leading-relaxed mb-8">
                  A AF Legacy Sports está a acompanhar de perto novos jogadores. Em breve, apresentaremos aqui o próximo talento em destaque.
                </p>
                <Link to="/jogadores" className="btn-outline">
                  Ver todos os jogadores <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ÁREA CLUBES */}
      <section className="relative py-28 md:py-40 overflow-hidden border-b border-white/5">
        <img src={IMAGES.stadiumNight} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B] via-[#0B0B0B]/85 to-[#0B0B0B]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="section-eyebrow mb-6">05 — Para clubes</div>
            <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-10">
              Talento avaliado. <br /><span className="text-[#A67C00]">Informação credível.</span>
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-10">
              A AF Legacy Sports pretende ser um parceiro estratégico dos clubes no processo
              de recrutamento e desenvolvimento de talento.
            </p>
            <Link to="/clubes-parceiros" data-testid="home-club-request-btn" className="btn-gold">
              Solicitar jogadores <ArrowRight size={14} />
            </Link>
          </div>
          <ul className="grid grid-cols-1 gap-px bg-white/5 border border-white/5 reveal">
            {[
              "Relatórios de scouting detalhados",
              "Jogadores previamente observados e avaliados",
              "Recomendação de atletas por perfil pretendido",
              "Monitorização contínua da evolução dos jogadores",
              "Apoio na identificação de talento nacional e internacional",
              "Comunicação organizada e profissional",
            ].map((t, i) => (
              <li key={i} className="bg-[#0B0B0B] p-6 flex items-center gap-4 text-neutral-200 text-sm">
                <span className="font-cinzel text-[#A67C00] text-xs">0{i + 1}</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PORQUÊ */}
      <section className="relative py-28 md:py-40 bg-[#0F0F0F] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="reveal mb-16 max-w-3xl">
            <div className="section-eyebrow mb-6">06 — Diferenciadores</div>
            <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight">
              Porquê a AF Legacy Sports
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {WHY.map((w, i) => (
              <div key={w} className="bg-[#0B0B0B] p-8 flex items-start gap-4 hover:bg-[#141414] transition-colors reveal">
                <div className="font-cinzel text-[#A67C00] text-xs tracking-[0.3em]">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-white text-sm leading-relaxed">{w}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARCERIAS */}
      <section className="relative py-28 md:py-40 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="section-eyebrow mb-6">07 — Parcerias</div>
            <h2 className="font-cinzel uppercase text-white text-3xl md:text-5xl font-semibold tracking-tight leading-tight mb-10">
              Construímos relações que <span className="text-[#A67C00]">desenvolvem talento</span>
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-10">
              A AF Legacy Sports procura estabelecer relações profissionais com entidades
              que partilham uma visão de desenvolvimento sustentável do futebol e dos atletas.
            </p>
            <Link to="/clubes-parceiros#parceria" data-testid="home-partnership-btn" className="btn-gold">
              Propor uma parceria <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5 reveal">
            {PARTNERS.map((p) => (
              <div key={p} className="bg-[#0B0B0B] p-6 text-neutral-300 text-sm">{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS PREVIEW */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-20 bg-[#0F0F0F] border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 reveal">
            <div>
              <div className="section-eyebrow mb-6">08 — Legacy News</div>
              <h2 className="font-cinzel uppercase text-white text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight">
                Últimas atualizações
              </h2>
            </div>
            <Link to="/legacy-news" className="btn-outline self-start" data-testid="home-news-btn">
              Ver todas <ArrowRight size={14} />
            </Link>
          </div>
          {news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {news.map((n) => (
                <Link
                  key={n.slug}
                  to={`/legacy-news/${n.slug}`}
                  data-testid={`news-preview-${n.slug}`}
                  className="group"
                >
                  <div className="relative aspect-[4/3] overflow-hidden zoom-parent mb-6">
                    <img src={n.image} alt={n.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute top-4 left-4 bg-[#A67C00] px-3 py-1 text-white text-[0.6rem] tracking-[0.3em] uppercase">
                      {n.category}
                    </div>
                  </div>
                  <div className="text-neutral-500 text-xs tracking-[0.25em] uppercase mb-3">
                    {new Date(n.date).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}
                  </div>
                  <h3 className="font-cinzel uppercase text-white text-xl leading-tight mb-3 group-hover:text-[#A67C00] transition-colors">
                    {n.title}
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed line-clamp-3">{n.summary}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-neutral-500 text-center py-12">Novas notícias em breve.</div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative pt-16 md:pt-24 pb-32 md:pb-40 overflow-hidden">
        <img src={IMAGES.locker} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/70 via-[#0B0B0B]/85 to-[#0B0B0B]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center reveal">
          <div className="section-eyebrow mb-8 text-center">09 — Chamada final</div>
          <h2 className="font-cinzel uppercase text-white text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05] mb-10">
            O teu talento pode ser <br /><span className="text-[#A67C00]">o início de um legado</span>
          </h2>
          <p className="text-neutral-300 text-lg mb-12 max-w-2xl mx-auto">
            Apresenta-nos o teu percurso, as tuas características e os teus objetivos.
            Cada perfil será analisado de forma individual pela nossa equipa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/submeter-perfil" data-testid="cta-submit-btn" className="btn-gold">
              Submeter perfil <ArrowRight size={14} />
            </Link>
            <Link to="/contactos" data-testid="cta-contact-btn" className="btn-outline">
              Falar com a equipa
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
